import React, { useState } from "react";
import useSWR from "swr";
import { PageHeader } from "@/components/Atoms";
import { Mail, Store, RefreshCw, Loader2, ShieldCheck, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ChangelogAdmin from "@/components/ChangelogAdmin";

const fetcher = (u) => api.get(u).then((r) => r.data);

function StoreAppCard({ app }) {
    return (
        <div className="cm-card p-4" data-testid={`souvera-storeapp-${app.app_id}`}>
            <div className="flex items-center gap-2">
                <Store size={15} className="text-teal-300 shrink-0" />
                <span className="text-xs text-zinc-200 font-mono">{app.label}</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 font-mono">{app.app_id}</div>
            <div className="text-[11px] text-zinc-600 mt-2">
                Nextcloud App Store — installed via occ on service activation.
            </div>
        </div>
    );
}

export default function SouveraWorkspacePage() {
    const { data: set } = useSWR("/api/admin/souvera/apps", fetcher);

    const [updBusy, setUpdBusy] = useState(false);
    const [updArmed, setUpdArmed] = useState(false);
    const [updRes, setUpdRes] = useState(null);
    const [updChannel, setUpdChannel] = useState("dev");
    const [err, setErr] = useState(null);

    const updateAllApps = async () => {
        if (!updArmed) { setUpdArmed(true); setTimeout(() => setUpdArmed(false), 5000); return; }
        setUpdArmed(false); setUpdBusy(true); setErr(null); setUpdRes(null);
        try {
            const r = await api.post(`/api/admin/souvera-workspace/update-all-apps?channel=${updChannel}`);
            setUpdRes(r.data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setUpdBusy(false);
    };

    const [dmarcBusy, setDmarcBusy] = useState(false);
    const [dmarcArmed, setDmarcArmed] = useState(false);
    const [dmarcRes, setDmarcRes] = useState(null);

    const registerAllDmarc = async () => {
        if (!dmarcArmed) { setDmarcArmed(true); setTimeout(() => setDmarcArmed(false), 5000); return; }
        setDmarcArmed(false); setDmarcBusy(true); setErr(null); setDmarcRes(null);
        try {
            const r = await api.post("/api/admin/souvera-workspace/dmarc/register-all");
            setDmarcRes(r.data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setDmarcBusy(false);
    };

    const [pmBusy, setPmBusy] = useState(false);
    const [pmArmed, setPmArmed] = useState(false);
    const [pmRes, setPmRes] = useState(null);

    const backfillAllPostmaster = async () => {
        if (!pmArmed) { setPmArmed(true); setTimeout(() => setPmArmed(false), 5000); return; }
        setPmArmed(false); setPmBusy(true); setErr(null); setPmRes(null);
        try {
            const r = await api.post("/api/admin/souvera-workspace/postmaster-backfill-all");
            setPmRes(r.data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setPmBusy(false);
    };

    return (
        <div data-testid="souvera-workspace-page">
            <PageHeader
                title="Souvera Workspace"
                subtitle="Changelogs, App Store apps & fleet actions. Deployment happens via git clone directly from GitHub."
                testid="souvera-header"
            />

            <div className="p-4 sm:p-6 space-y-6">
                {/* Fleet actions */}
                <div className="cm-card p-4" data-testid="souvera-fleet-actions">
                    <div className="flex items-center gap-2 mb-3">
                        <RefreshCw size={14} className="text-cm-brand" />
                        <span className="cm-eyebrow">Fleet actions — confirm by clicking twice</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            value={updChannel}
                            onChange={(e) => setUpdChannel(e.target.value)}
                            disabled={updBusy}
                            data-testid="souvera-update-all-channel-select"
                            className="cm-input max-w-[110px] font-mono text-xs py-1.5"
                        >
                            <option value="dev">Dev</option>
                            <option value="stable">Stable</option>
                        </select>
                        <button
                            data-testid="souvera-update-all-apps-btn"
                            onClick={updateAllApps}
                            disabled={updBusy}
                            className={`cm-btn-secondary cm-btn-sm inline-flex items-center gap-1.5 disabled:opacity-50 ${updArmed ? "!bg-cm-warning !text-cm-bg !border-cm-warning font-bold" : ""}`}
                        >
                            {updBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                            {updBusy ? "Queueing…" : updArmed ? "Confirm? All" : "Apps: all workspaces"}
                        </button>
                        <button
                            data-testid="souvera-dmarc-register-all-btn"
                            onClick={registerAllDmarc}
                            disabled={dmarcBusy}
                            className={`cm-btn-secondary cm-btn-sm inline-flex items-center gap-1.5 disabled:opacity-50 ${dmarcArmed ? "!bg-cm-warning !text-cm-bg !border-cm-warning font-bold" : ""}`}
                        >
                            {dmarcBusy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                            {dmarcBusy ? "Registering…" : dmarcArmed ? "Confirm? All" : "DMARC: all"}
                        </button>
                        <button
                            data-testid="souvera-postmaster-backfill-all-btn"
                            onClick={backfillAllPostmaster}
                            disabled={pmBusy}
                            className={`cm-btn-secondary cm-btn-sm inline-flex items-center gap-1.5 disabled:opacity-50 ${pmArmed ? "!bg-cm-warning !text-cm-bg !border-cm-warning font-bold" : ""}`}
                        >
                            {pmBusy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                            {pmBusy ? "Queueing…" : pmArmed ? "Confirm? All" : "Postmaster: all"}
                        </button>
                    </div>
                </div>

                {err && (
                    <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2 font-mono" data-testid="souvera-err">
                        {err}
                    </div>
                )}

                {updRes && (
                    <div className="text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-3 py-2 text-emerald-200 font-mono space-y-1" data-testid="souvera-update-all-result">
                        <b>{updRes.enqueued_count}</b> workspace update(s) queued · {updRes.skipped_count} skipped (of {updRes.total_souvera_clouds} Souvera clouds).
                    </div>
                )}

                {dmarcRes && (
                    <div className="text-xs bg-sky-500/10 border border-sky-500/30 rounded-sm px-3 py-2 text-sky-200 font-mono space-y-1" data-testid="souvera-dmarc-register-all-result">
                        <b>{dmarcRes.registered_count}</b> mail domain(s) registered · {dmarcRes.skipped_count} skipped · {dmarcRes.failed_count} failed.
                    </div>
                )}

                {pmRes && (
                    <div className="text-xs bg-violet-500/10 border border-violet-500/30 rounded-sm px-3 py-2 text-violet-200 font-mono space-y-1" data-testid="souvera-postmaster-backfill-all-result">
                        <b>{pmRes.enqueued_count}</b> Postmaster backfill(s) queued · {pmRes.skipped_count} skipped.
                    </div>
                )}

                <div className="cm-card px-3 py-2 border border-zinc-800 text-[11px] text-zinc-500 font-mono leading-relaxed">
                    Deployment: <span className="text-zinc-300">git clone directly from GitHub</span>.
                    Apps then self-update autonomously via <span className="text-zinc-300">occ souvera_*:self-update</span>.
                    Changelogs are maintained here and delivered to Souvera Central via the <span className="text-zinc-300">public API endpoint</span>.
                </div>

                {/* Changelog-Admin */}
                <div className="cm-card p-0 border border-zinc-800" data-testid="souvera-changelogs">
                    <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center gap-2">
                        <FileText size={12} /> Changelogs
                    </div>
                    <div className="p-3">
                        <ChangelogAdmin />
                    </div>
                </div>

                {/* Store-Apps (aus App-Store, kein Build) */}
                <div className="cm-card p-0 border border-zinc-800" data-testid="souvera-store-apps">
                    <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center gap-2">
                        <Store size={12} /> App Store apps
                    </div>
                    <div className="p-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {(set?.store_apps || []).map((a) => (
                                <StoreAppCard key={a.app_id} app={a} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
